<script setup lang="ts">
import type { HTMLAttributes } from "vue"
import { computed } from "vue"
import { TimeSelect } from "@/shared/ui/time-select"

interface TimeRangeValue {
  start: number
  end: number
}

const props = withDefaults(
  defineProps<{
    modelValue: TimeRangeValue
    min?: number
    max?: number
    step?: number
    disabled?: boolean
    invalid?: boolean
    id?: string
    ariaLabelledby?: string
    startLabel?: string
    endLabel?: string
    class?: HTMLAttributes["class"]
  }>(),
  {
    min: 0,
    max: 1440,
    step: 15,
  },
)

const hasLabels = computed(() => !!(props.startLabel || props.endLabel))

const emit = defineEmits<{
  "update:modelValue": [value: TimeRangeValue]
}>()

const endMin = computed(() => props.modelValue.start + props.step)

function onStartChange(newStart: number): void {
  if (newStart >= props.modelValue.end) {
    const newEnd = Math.min(newStart + props.step, props.max)
    emit("update:modelValue", { start: newStart, end: newEnd })
  } else {
    emit("update:modelValue", { start: newStart, end: props.modelValue.end })
  }
}

function onEndChange(newEnd: number): void {
  emit("update:modelValue", { start: props.modelValue.start, end: newEnd })
}
</script>

<template>
  <div
    :id="id"
    :class="[props.class, 'flex items-center gap-2']"
    :aria-labelledby="ariaLabelledby"
    role="group"
  >
    <div :class="startLabel ? 'flex flex-col gap-[3px]' : 'contents'">
      <span
        v-if="startLabel"
        class="text-[10px] font-semibold text-muted-foreground whitespace-nowrap"
        aria-hidden="true"
      >{{ startLabel }}</span>
      <TimeSelect
        :model-value="modelValue.start"
        :min="min"
        :max="max"
        :step="step"
        :disabled="disabled"
        :invalid="invalid"
        @update:model-value="onStartChange"
      />
    </div>
    <span
      aria-hidden="true"
      :class="['shrink-0 text-muted-foreground', hasLabels ? 'self-end mb-[9px]' : '']"
    >—</span>
    <div :class="endLabel ? 'flex flex-col gap-[3px]' : 'contents'">
      <span
        v-if="endLabel"
        class="text-[10px] font-semibold text-muted-foreground whitespace-nowrap"
        aria-hidden="true"
      >{{ endLabel }}</span>
      <TimeSelect
        :model-value="modelValue.end"
        :min="endMin"
        :max="max"
        :step="step"
        :disabled="disabled"
        :invalid="invalid"
        @update:model-value="onEndChange"
      />
    </div>
  </div>
</template>
